import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

type ToastProps = {
  message: string;
  duration?: number;
  onClose: () => void;
  type?: 'success' | 'error';
};

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

export const Toast: React.FC<ToastProps> = ({ message, duration = 3000, onClose, type = 'success' }) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // duration <= 0 => "sticky" toast, только ручное закрытие
    if (duration == null || duration <= 0) return;

    const timer = setTimeout(() => {
      setIsClosing(true);
      setTimeout(() => {
        onClose();
      }, 300); // Wait for animation to complete
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <ToastBox $isClosing={isClosing} $type={type}>
      <ToastMessage>{message}</ToastMessage>
      <CloseButton onClick={handleClose}>×</CloseButton>
    </ToastBox>
  );
};

const ToastBox = styled.div<{ $isClosing: boolean; $type: 'success' | 'error' }>`
  background: ${props => props.$type === 'error' 
    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 50%, #c44569 100%)'
    : 'linear-gradient(135deg, #6777FF 0%, #B7BFFF 100%)'};
  color: #fff;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 300px;
  max-width: 500px;
  animation: ${props => props.$isClosing ? slideDown : slideUp} 0.3s ease;
`;

const ToastMessage = styled.div`
  flex: 1;
  font-size: 14px;
  color: #fff;
  line-height: 1.4;
  white-space: pre-line;
`;

const CloseButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

// Toast Manager
let toastQueue: Array<{ id: string; message: string; duration?: number; type?: 'success' | 'error' }> = [];
let listeners: Array<() => void> = [];
const MAX_TOASTS = 3;

export const showToast = (message: string, duration?: number, type: 'success' | 'error' = 'success') => {
  const id = Math.random().toString(36).substr(2, 9);
  
  // If we already have max toasts, remove the oldest one
  if (toastQueue.length >= MAX_TOASTS) {
    toastQueue.shift(); // Remove first (oldest) element
  }
  
  toastQueue.push({ id, message, duration, type });
  listeners.forEach(l => l());
};

export const removeToast = (id: string) => {
  toastQueue = toastQueue.filter(t => t.id !== id);
  listeners.forEach(l => l());
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; duration?: number; type?: 'success' | 'error' }>>([]);

  useEffect(() => {
    const updateToasts = () => {
      setToasts([...toastQueue]);
    };

    listeners.push(updateToasts);
    updateToasts();

    return () => {
      listeners = listeners.filter(l => l !== updateToasts);
    };
  }, []);

  return (
    <ToastWrapper>
      {toasts.map((toast, index) => (
        <ToastWrapperItem key={toast.id} $index={index}>
          <Toast
            message={toast.message}
            duration={toast.duration}
            type={toast.type || 'success'}
            onClose={() => removeToast(toast.id)}
          />
        </ToastWrapperItem>
      ))}
    </ToastWrapper>
  );
};

const ToastWrapper = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10001;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  pointer-events: none;
`;

const ToastWrapperItem = styled.div<{ $index: number }>`
  pointer-events: auto;
`;

